#import '../../../graphql/fragments/common.gql'
fragment TariffScheduleCommonInformationFragment on TariffScheduleCommonInformation {
  name {
    ...LangField
  }
  visitTimetableCount
  hotelSetHasRemote
  applyingRules {
    penaltyScheme {
      description
      name
      shortName
    }
    stopSalePeriodHours
    supplierPayRule {
      description
      name
      shortName
    }
  }
  checkInOutSettings {
    checkInTime
    checkOutTime
    nextDayCheckout
  }
  taxScheme {
    cid
    name
  }
}

fragment TariffScheduleCommonInformationUpdatedFragment on TariffScheduleCommonInformationUpdated {
  name {
    ...LangField
  }
  visitTimetableCount
  hotelSetHasRemote
  applyingRules {
    penaltyScheme {
      description
      name
      shortName
    }
    stopSalePeriodHours
    supplierPayRule {
      description
      name
      shortName
    }
  }
  checkInOutSettings {
    checkInTime
    checkOutTime
    nextDayCheckout
  }
  taxScheme {
    cid
    name
  }
  hasServiceSequenceChanged
}

fragment TariffScheduleFragment on TariffSchedule2View {
  baseSettings {
    ageSettings {
      categories {
        values {
          from
          to
        }
      }
      determineAgeMethod
      initialAge
      noSeatAge
    }
    completed
    roomCategories {
      roomCategoryCid
      quotaStatus
    }
  }
  contractType
  status
  commonInformation {
    ...TariffScheduleCommonInformationFragment
  }
  tariffScheduleCid
  hotelSetCid
  operations {
    sendInWork
    startPVM
    update
    canEditPlacingVariants
  }
  remoteInteraction {
    activeRemoting
    channelManagerCode
    interactionId
    remoteIntegrationUrl
  }
}

query TariffSchedules($contractNumber: Int!) {
  tariffSchedules2(contractNumber: $contractNumber) {
    operations {
      canAddExtranetTariffSchedule
      canAddRemoteTariffSchedule
    }
    extranet {
      tariffScheduleCid
      name {
        ...LangField
      }
      stopSalePeriodHours
      status
    }
    remote {
      channelManagerCode
      interactionId
      name {
        value
        langCode
      }
      tariffSchedule {
        activeRemoting
        tariffScheduleCid
        contractClosed
      }
    }
  }
}

fragment TariffScheduleGridView on TariffScheduleGridView {
  operations {
    edit
  }
  invalidCommissions
  baseSettings {
    currencyIsoCode
    period {
      from
      to
    }
  }
  tabs {
    ...Tabs
  }
}

query SupplierPayRuleList {
  supplierPayRuleList {
    shortName
    name
  }
}

query PenaltySchemeList {
  penaltySchemeList {
    shortName
    name
  }
}

query GetTaxSchemeList {
  taxSchemeList {
    cid
    name
  }
}

query GetTariffScheduleGrid($tariffScheduleCid: String!) {
  tariffScheduleGrid(tariffScheduleCid: $tariffScheduleCid) {
    ...TariffScheduleGridView
  }
}
mutation AddTariffSchedule($addTariffScheduleInput: AddTariffScheduleInput!) {
  addTariffSchedule(addTariffScheduleInput: $addTariffScheduleInput) {
    tariffScheduleCid
    tariffSchedule {
      ...TariffScheduleFragment
    }
    errors {
      ...Error
    }
  }
}

query GetTariffSchedule($tariffScheduleCid: String!) {
  tariffSchedule2(tariffScheduleCid: $tariffScheduleCid) {
    ...TariffScheduleFragment
  }
}

mutation EditTariffScheduleStart($input: EditTariffScheduleInput!) {
  editTariffSchedule(input: $input) {
    TariffSchedule {
      ...TariffScheduleCommonInformationFragment
    }
    errors {
      ...Error
      ...LockResourceError
    }
  }
}

mutation EditTariffScheduleKeepAlive($input: KeepAliveTariffScheduleInput!) {
  keepAliveTariffSchedule(input: $input) {
    status
    errors {
      ...Error
    }
  }
}

mutation EditTariffScheduleCancel($input: CancelEditTariffScheduleInput!) {
  cancelEditTariffSchedule(input: $input) {
    status
    errors {
      ...Error
    }
  }
}

mutation EditTariffScheduleUpdate($input: UpdateTariffScheduleInput!) {
  updateTariffSchedule(input: $input) {
    TariffSchedule {
      ...TariffScheduleCommonInformationUpdatedFragment
    }
    errors {
      ...Error
    }
  }
}

fragment PlacingVariantTarification on PlacingVariantTarificationView {
  name
  placingVariant {
    baseSeats {
      touristType {
        ageCategory {
          from
          to
        }
        isAdult
      }
      seats
    }
    extSeats {
      touristType {
        ageCategory {
          from
          to
        }
        isAdult
      }
      seats
    }
  }
  tarification {
    roomPlacingCid
    pricePerRoom
    isNeedConfirm
    maxNoSeatQuantity
    defaultAvailable
  }
  isChecked
}

fragment PlacingVariantTarificationFragment on PlacingVariantTarificationView {
  isChecked
  name
  placingVariant {
    baseSeats {
      seats
      touristType {
        isAdult
        ageCategory {
          from
          to
        }
      }
    }
    extSeats {
      seats
      touristType {
        isAdult
        ageCategory {
          from
          to
        }
      }
    }
  }
  tarification {
    defaultAvailable
    isNeedConfirm
    maxNoSeatQuantity
    pricePerRoom
    roomPlacingCid
  }
}

fragment RoomCategoryPlacingsFragment on RoomCategoryPlacingsView {
  roomCategory {
    cid
    description {
      langCode
      value
    }
    name {
      langCode
      value
    }
    shortName
    type {
      cid
      name
    }
  }
  suggestedPlacingVariants {
    ...PlacingVariantTarificationFragment
  }
  conflictedPlacingVariants {
    ...PlacingVariantTarificationFragment
  }
}

query GetPlacingVariants($hotelSetCid: String!) {
  placingVariants(hotelSetCid: $hotelSetCid) {
    placingVariants {
      ...RoomCategoryPlacingsFragment
    }
  }
}

mutation PvmStart($tariffScheduleCid: String!) {
  placingVariantsMasterStart(input: { tariffScheduleCid: $tariffScheduleCid }) {
    settings {
      roomCategories {
        roomCategoryCid
        quotaStatus
      }
      ageSettings {
        initialAge
        noSeatAge
        determineAgeMethod
        categories {
          values {
            from
            to
          }
        }
      }
    }
    errors {
      ...Error
      ...LockResourceError
    }
  }
}

mutation PvmKeepAlive($tariffScheduleCid: String!) {
  placingVariantsMasterKeepAlive(input: { tariffScheduleCid: $tariffScheduleCid }) {
    status
    errors {
      ...Error
    }
  }
}

mutation PvmCancelEdit($tariffScheduleCid: String!) {
  placingVariantsMasterCancel(input: { tariffScheduleCid: $tariffScheduleCid }) {
    status
    errors {
      ...Error
    }
  }
}

mutation PvmComplete($input: PlacingVariantsMasterCompleteInput!) {
  placingVariantsMasterComplete(input: $input) {
    errors {
      ...Error
    }
  }
}

mutation PvmGenerate($input: PlacingVariantsMasterGenerateInput!) {
  placingVariantsMasterGenerate(input: $input) {
    placingVariantsTarification {
      ...RoomCategoryPlacingsFragment
    }
    invalidRoomCategoryCids
    errors {
      ...Error
    }
  }
}

mutation StartPlacingVariantsEdit($tariffScheduleCid: String!) {
  placingVariantsEdit(input: { tariffScheduleCid: $tariffScheduleCid }) {
    placingVariantsTarification {
      ...RoomCategoryPlacingsFragment
    }
    errors {
      ...Error
      ...LockResourceError
    }
  }
}

mutation KeepAlivePlacingVariantsEdit($tariffScheduleCid: String!) {
  placingVariantsKeepAlive(input: { tariffScheduleCid: $tariffScheduleCid }) {
    status
    errors {
      ...Error
    }
  }
}

mutation UpdatePlacingVariants($variants: PlacingVariantsUpdateInput!) {
  placingVariantsUpdate(input: $variants) {
    placingVariantsTarification {
      ...RoomCategoryPlacingsFragment
    }
    errors {
      ...Error
    }
  }
}

mutation CancelPlacingVariantsEdit($tariffScheduleCid: String!) {
  placingVariantsCancelEdit(input: { tariffScheduleCid: $tariffScheduleCid }) {
    status
    errors {
      ...Error
    }
  }
}
